import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Box, Stack, Grid } from "@mui/material";
import VSNTypography from "../../../shared/VSNTypography";
import useOTBStyle from "../OTBStyle";
import {
  CPETIcon,
  Cube,
  FrontLineNewLogo,
  ProvideIcon,
  UltraSound,
  VitalsIcon,
  Xray,
} from "../../../shared/icon";
import VSNImage from "../../../shared/VSNImage";

const categories = [
  { key: "labs", label: "Labs", icon: <Cube /> },
  { key: "vitals", label: "Vitals", icon: <VitalsIcon /> },
  { key: "x_ray", label: "X-ray", icon: <Xray /> },
  { key: "cpet", label: "CPET", icon: <CPETIcon /> },
  { key: "ultrasound", label: "Ultrasound", icon: <UltraSound /> },
  { key: "provider_visit", label: "Provider Visit", icon: <ProvideIcon /> },
];

const Completion = ({ data, pdf = false, otbInfo }) => {
  const classes = useOTBStyle();
  const [chartData, setChartData] = useState({
    totalComplete: data?.length,
    PAQIncomplete: data?.filter((patient) => patient.paq === 0)?.length,
  });
  const [selectedValue, setSelectedValue] = useState("");
  const getFilteredCount = (category) =>
    data?.filter((patient) => patient[category] === 1).length || 0;

  const handleCardClick = (category) => {
    if (category === selectedValue) {
      setChartData({
        totalComplete: data?.length,
        PAQIncomplete: data?.filter((patient) => patient.paq === 0)?.length,
      });
      setSelectedValue("");
    } else {
      setSelectedValue(category);
      const filteredData = data?.filter((patient) => patient[category] === 1);
      const totalComplete = filteredData?.length;
      const PAQIncomplete = filteredData?.filter(
        (patient) => patient.paq === 0
      ).length;
      setChartData({
        totalComplete,
        PAQIncomplete,
      });
    }
  };

  const option = {
    polar: {
      radius: [30, "95%"],
    },
    angleAxis: {
      max: data?.length,
      startAngle: 90,
      endAngle: -180,
      show: true,
      axisLabel: {
        show: false,
        color: "#000",
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    radiusAxis: {
      type: "category",
      data: ["PAQ Incomplete", "Total Complete"],
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: { show: false },
    },
    tooltip: {},
    series: [
      {
        type: "bar",
        data: [
          {
            value: chartData.PAQIncomplete,
            itemStyle: {
              color: "#9F7E50",
              barBorderRadius: "5%",
            },
          },
          {
            value: chartData.totalComplete,
            itemStyle: {
              color: "#D5B98B",
              barBorderRadius: "5%",
            },
          },
        ],
        coordinateSystem: "polar",
        label: {
          show: true,
          position: "insideStart",
          distance: -120,
          formatter: (params) => {
            if (params.dataIndex === 0) {
              return `${params.value} | PAQ Incomplete`;
            }
            if (params.dataIndex === 1) {
              return `${params.value} | Total Complete`;
            }
            return "";
          },
          color: "#000",
        },
        showBackground: true,
        backgroundStyle: {
          color: "rgba(180, 180, 180, 0.0)",
        },
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
      },
    ],
  };

  useEffect(() => {
    setChartData({
      totalComplete: data?.length,
      PAQIncomplete: data?.filter((patient) => patient.paq === 0)?.length,
    });
  }, [data]);

  return (
    <>
      {pdf && (
        <>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <Stack
              container
              justifyContent="space-between"
              alignItems="center"
              direction="row"
              gap={4}
              px={4}
              minHeight="720px"
            >
              <Stack>
                <Box className={classes.mainLayout}>
                  <VSNTypography variant="h5" component="h5">
                    Operational Threat Brief by
                  </VSNTypography>

                  <Box>
                    <FrontLineNewLogo />
                  </Box>

                  <VSNTypography
                    sx={{
                      marginBottom: "8px !important",
                      fontSize: "24px !important",
                      fontWeight: "700 !important",
                      color: "#747787 !important",
                    }}
                  >
                    For
                  </VSNTypography>

                  <VSNTypography
                    sx={{
                      lineHeight: "50px !important",
                      fontSize: "50px !important",
                      fontWeight: "700 !important",
                      color: "#0A0A0B !important",
                    }}
                  >
                    {otbInfo?.data?.name}
                  </VSNTypography>

                  {otbInfo?.data?.participation_policy && (
                    <>
                      <VSNTypography
                        sx={{
                          fontSize: "20px !important",
                          marginBottom: "0px !important",
                        }}
                      >
                        Participation Policy:
                      </VSNTypography>

                      <VSNTypography
                        sx={{
                          color: "#000 !important",
                          marginBottom: "24px !important",
                        }}
                      >
                        {otbInfo?.data?.participation_policy}
                      </VSNTypography>
                    </>
                  )}
                </Box>
              </Stack>

              <Stack>
                <Box align="center" className={classes.PdfPsychometricImage}>
                  <VSNImage src={otbInfo?.data?.logo ?? null} alt="info-img" />
                </Box>
              </Stack>
            </Stack>
          </Box>

          <Box
            className={classes.pdfReportLayout}
            sx={{
              pageBreakAfter: "always",
            }}
          >
            <VSNTypography variant="h4">Completion</VSNTypography>
            <Stack gap={2}>
              <Stack direction="row" alignItems="center">
                <Box sx={{ width: "100%" }}>
                  <ReactECharts
                    option={option}
                    style={{ height: "350px", width: "350px" }}
                  />
                </Box>
                <Box sx={{ width: "100%", textAlign: "center" }}>
                  <VSNTypography variant="h2">
                    Questionnaire Completion
                  </VSNTypography>
                  <VSNTypography variant="h5">
                    This Includes:
                    <br /> PAQ (Pre-Arrival Questionnaire), <br />
                    HPA (Human Performance Assessment),
                    <br />
                    OCA (Organizational Climate Assessment)
                  </VSNTypography>
                </Box>
              </Stack>

              <Grid container>
                {categories?.map(({ key, label, icon }, index) => (
                  <Grid
                    item
                    xs={4}
                    sm={4}
                    md={4}
                    lg={4}
                    gap={2}
                    p={1}
                    key={index}
                  >
                    <Box
                      key={key}
                      className={classes.pdfReportCard}
                      m={2}
                      p={2}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        {icon}
                        <VSNTypography variant="h2">{label}</VSNTypography>
                      </Stack>
                      <VSNTypography className="record">
                        {getFilteredCount(key)}
                        <span> / {data.length} </span>
                        <span
                          style={{
                            color:
                              getFilteredCount(key) < data.length
                                ? "red"
                                : "green",
                          }}
                        >
                          ({getFilteredCount(key) - data.length})
                        </span>
                      </VSNTypography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* <Stack direction="row" gap={2}>
              {[
                { key: "cpet", label: "CPET", icon: CPETIcon },
                {
                  key: "ultrasound",
                  label: "Ultrasound",
                  icon: UltraSound,
                },
                {
                  key: "provider_visit",
                  label: "Provider Visit",
                  icon: ProvideIcon,
                },
              ].map(({ icon: Icon, label, key }) => (
                <Box key={key} className={classes.pdfReportCard}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Icon />
                    <VSNTypography variant="h2">{label}</VSNTypography>
                  </Stack>
                  <VSNTypography className="record">
                    {getFilteredCount(key)}
                    <span> / {data.length} </span>
                    <span
                      style={{
                        color:
                          getFilteredCount(key) < data.length ? "red" : "green",
                      }}
                    >
                      ({getFilteredCount(key) - data.length})
                    </span>
                  </VSNTypography>
                </Box>
              ))}
            </Stack> */}
            </Stack>
          </Box>
        </>
      )}

      {!pdf && (
        <Box
          className={classes.reportLayout}
          sx={{ display: pdf ? "none" : "block" }}
        >
          <VSNTypography variant="h2">Completion</VSNTypography>
          <Grid
            container
            justifyContent="center"
            spacing={3}
            flex={1}
            alignItems="center"
          >
            <Grid item xxl={5} xlg={6} xs={12}>
              <Stack alignItems="center" mb={{ xlg: 0, xs: 3 }}>
                <ReactECharts
                  option={option}
                  style={{ height: "350px", width: "100%" }}
                />
                <VSNTypography variant="h2" textAlign="center">
                  Questionnaire Completion
                </VSNTypography>
                <VSNTypography variant="h5" textAlign="center">
                  This Includes:
                  <br />
                  PAQ (Pre-Arrival Questionnaire),
                  <br />
                  HPA (Human Performance Assessment),
                  <br />
                  OCA (Organizational Climate Assessment)
                </VSNTypography>
              </Stack>
            </Grid>
            <Grid item xxl={5} xlg={6} xs={12}>
              <Grid container columnSpacing={2}>
                {categories?.map(({ key, label, icon }) => {
                  const completedCount =
                    data?.filter((patient) => patient[key] === 1).length || 0;
                  const remaining = completedCount - data?.length;
                  return (
                    <Grid item key={key} xlg={6} lg={3} md={4} xsm={6} xs={12}>
                      <Box
                        onClick={() => handleCardClick(key)}
                        className={classes.reportCard}
                        sx={{ cursor: "pointer" }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          {icon}
                          <VSNTypography className={classes.reportCardHeading}>
                            {label}
                          </VSNTypography>
                        </Stack>
                        <VSNTypography className="record">
                          {completedCount} / {data?.length}
                          <span
                            style={{ color: remaining < 0 ? "red" : "green" }}
                          >
                            {" "}
                            ({remaining})
                          </span>
                        </VSNTypography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default Completion;
